import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createBanner } from "../../../RTK/categories/BannerSlice";
import { X, Upload, Loader2, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";

const AddNewBannerPopup = ({ isVisible, onClose }) => {
  const dispatch = useDispatch();
  const { isCreating, createError } = useSelector((state) => state.banners);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: null,
    is_active: true,
  });
  const [imagePreview, setImagePreview] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  if (!isVisible) return null;

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) {
      errors.title = "عنوان البانر مطلوب";
    }
    if (!formData.image) {
      errors.image = "صورة البانر مطلوبة";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      handleFileChange(files[0]);
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (validationErrors[name]) {
        setValidationErrors((prev) => ({ ...prev, [name]: "" }));
      }
    }
  };

  const handleFileChange = (file) => {
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("الرجاء اختيار ملف صورة صالح");
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("حجم الصورة يجب أن يكون أقل من 5 ميجابايت");
        return;
      }

      setFormData((prev) => ({ ...prev, image: file }));
      if (validationErrors.image) {
        setValidationErrors((prev) => ({ ...prev, image: "" }));
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileChange(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const form = new FormData();
    form.append("title", formData.title.trim());
    form.append("description", formData.description.trim());
    form.append("is_active", formData.is_active);
    if (formData.image) {
      form.append("image", formData.image);
    }

    try {
      const result = await dispatch(createBanner(form));
      if (!result.error) {
        toast.success("تم إضافة البانر بنجاح");
        onClose();
      } else {
        toast.error(createError || "حدث خطأ أثناء إضافة البانر");
      }
    } catch (error) {
      toast.error("حدث خطأ غير متوقع");
    }
  };

  const renderError = (field) => {
    if (validationErrors[field]) {
      return (
        <div className="flex items-center gap-1 mt-1 text-red-500 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{validationErrors[field]}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
      dir="rtl"
    >
      <div className="relative bg-gray-900 rounded-xl w-full max-w-2xl overflow-hidden border border-gray-800">
        {/* Close Button */}
        <div className="absolute left-4 top-4">
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors"
            aria-label="إغلاق"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <h2 className="text-2xl font-bold text-white mb-6">
            إضافة بانر جديد
          </h2>

          <div className="space-y-6">
            {/* Image Upload */}
            <div>
              <div
                onClick={handleImageClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`mt-1 border-2 border-dashed rounded-lg transition-all cursor-pointer
                  ${
                    validationErrors.image
                      ? "border-red-500"
                      : isDragging
                      ? "border-yellow-500 bg-yellow-500/10"
                      : "border-gray-700 hover:border-yellow-500/50"
                  }`}
              >
                <div className="flex justify-center px-6 pt-5 pb-6">
                  <div className="space-y-1 text-center">
                    {imagePreview ? (
                      <div className="relative group">
                        <img
                          src={imagePreview}
                          alt="معاينة البانر"
                          className="max-h-48 rounded-lg mx-auto"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                          <p className="text-white text-sm">
                            انقر لتغيير الصورة
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-400">
                          اسحب وأفلت الصورة هنا، أو انقر للاختيار
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          PNG, JPG, GIF حتى 5 ميجابايت
                        </p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleInputChange}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
              {renderError("image")}
            </div>

            {/* Title */}
            <div>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full bg-gray-800 text-white px-4 py-2.5 rounded-lg border transition-colors
                  ${
                    validationErrors.title
                      ? "border-red-500"
                      : "border-gray-700 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                  }`}
                placeholder="أدخل عنوان البانر"
              />
              {renderError("title")}
            </div>

            {/* Description */}
            <div>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full bg-gray-800 text-white px-4 py-2.5 rounded-lg border border-gray-700 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors resize-none"
                placeholder="أدخل وصف البانر (اختياري)"
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                id="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-700 rounded"
              />
              <label htmlFor="is_active" className="mr-2 text-sm text-gray-300">
                تفعيل البانر
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-6">
            <button
              type="submit"
              disabled={isCreating || !formData.image || !formData.title}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>جاري الإضافة...</span>
                </>
              ) : (
                <span>إضافة البانر</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNewBannerPopup;
